import { motion } from 'framer-motion'
import { LCARSBar, Panel } from '@enterpress/holomatrix'
import { Sparkles, Server, Bot, Handshake } from 'lucide-react'
import { useTranslation } from '../i18n/index'

/**
 * Mission Section - The "Why" of EnterPress
 * 
 * Establishes the core mission: empowering the WordPress community
 * with state-of-the-art AI capabilities
 */
export function Mission() {
  const { t } = useTranslation()
  const pillars = [
    {
      icon: Server,
      title: t('mission.pillar1Title'),
      description: t('mission.pillar1Desc'),
      color: '#00D4FF',
    },
    {
      icon: Bot,
      title: t('mission.pillar2Title'),
      description: t('mission.pillar2Desc'),
      color: '#9D8CFF',
    },
    {
      icon: Handshake,
      title: t('mission.pillar3Title'),
      description: t('mission.pillar3Desc'),
      color: '#FFB347',
    },
  ]

  return (
    <section id="mission" className="py-24 px-6 relative">
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-[rgba(0,212,255,0.02)] via-transparent to-transparent pointer-events-none" />

      <div className="max-w-6xl mx-auto relative">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <LCARSBar color="cyan" size="md" className="w-20" />
            <h2 className="text-sm font-bold tracking-[4px] uppercase text-[#00D4FF]">
              {t('mission.title')}
            </h2>
            <LCARSBar color="cyan" size="md" className="w-20" />
          </div>
          
          {/* Mission statement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mb-12"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <Sparkles size={24} className="text-[#FFB347]" />
              <span className="text-lg text-[#FFB347] font-medium">{t('mission.purpose')}</span>
            </div>
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-4xl mx-auto">
              {t('mission.statementPart1')}{' '}
              <span className="bg-gradient-to-r from-[#00D4FF] to-[#9D8CFF] bg-clip-text text-transparent">
                {t('mission.statementHighlight')}
              </span>{' '}
              {t('mission.statementPart2')}
            </h3>
          </motion.div>

          <p className="text-lg text-[rgba(232,244,248,0.6)] max-w-2xl mx-auto">
            {t('mission.description')}
          </p>
        </motion.div>

        {/* Three pillars */}
        <div className="grid md:grid-cols-3 gap-6">
          {pillars.map((pillar, index) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
            >
              <Panel brackets className="p-6 h-full text-center">
                <motion.div
                  className="w-16 h-16 mx-auto mb-5 rounded-2xl flex items-center justify-center"
                  style={{ 
                    backgroundColor: `${pillar.color}15`,
                    border: `1px solid ${pillar.color}40`,
                  }}
                  whileHover={{ 
                    scale: 1.05,
                    boxShadow: `0 0 30px ${pillar.color}30`,
                  }}
                  transition={{ duration: 0.2 }}
                >
                  <pillar.icon size={28} style={{ color: pillar.color }} />
                </motion.div>
                <h4 className="text-xl font-bold text-white mb-3">
                  {pillar.title}
                </h4>
                <p className="text-[rgba(232,244,248,0.6)] leading-relaxed">
                  {pillar.description}
                </p>
              </Panel>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

